import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { renew, useAuth } from '../auth/useAuth.ts';
import { config } from '../config.ts';
import { currentLocale } from '../i18n/index.ts';
import { ApiRequestError, createApiClient } from './client.ts';
import { ApiContext, LiveContext } from './hooks.ts';
import { createLiveFeed, liveUrl } from './live.ts';

export function ApiProvider({ children }: { children: ReactNode }) {
  const { getAccessToken } = useAuth();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: (failures, error) =>
              !(error instanceof ApiRequestError && error.status >= 400 && error.status < 500) &&
              failures < 2,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );
  const api = useMemo(
    () =>
      createApiClient({
        baseUrl: config.apiBaseUrl,
        getAccessToken,
        refreshAccessToken: async () => (await renew())?.access_token ?? null,
        getLocale: currentLocale,
      }),
    [getAccessToken],
  );
  const live = useMemo(
    () => createLiveFeed({ url: liveUrl(config.apiBaseUrl, window.location), getAccessToken }),
    [getAccessToken],
  );
  return (
    <ApiContext value={api}>
      <LiveContext value={live}>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </LiveContext>
    </ApiContext>
  );
}
